// frontend/src/api/unbounceApi.js
// Created: April 8, 2025
// This file contains functions for interacting with the Unbounce API

import unbounceClient from './unbounceClient';
import { getToken } from '../auth/auth';
import { API_URL } from '../config';

// Cache for API responses
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Refresh the Unbounce token
 * @returns {Promise<Object>} The result of the token refresh
 */
export const refreshUnbounceToken = async () => {
  try {
    const response = await fetch(`${API_URL}/api/admin/unbounce/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      credentials: 'include'
    });

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to refresh token');
    }
    
    return data;
  } catch (error) {
    console.error('Error refreshing Unbounce token:', error);
    throw error;
  }
};

/**
 * Reconnect to Unbounce
 * @returns {Promise<Object>} The result of the reconnection
 */
export const reconnectUnbounce = async () => {
  try {
    const response = await fetch(`${API_URL}/api/admin/unbounce/reconnect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      credentials: 'include'
    });

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to reconnect to Unbounce');
    }
    
    return data;
  } catch (error) {
    console.error('Error reconnecting to Unbounce:', error);
    throw error;
  }
};

/**
 * Fetch leads from Unbounce
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} The leads data
 */
export const fetchUnbounceLeads = async (params = {}) => {
  try {
    const response = await unbounceClient.get('/admin/unbounce/leads', { params });
    return response.data;
  } catch (error) {
    if (error.needsReconnect) {
      // Try to reconnect
      await reconnectUnbounce();
      // Retry the original request
      return unbounceClient.get('/admin/unbounce/leads', { params });
    }
    throw error;
  }
};

/**
 * Sync leads from Unbounce
 * @returns {Promise<Object>} The result of the sync operation
 */
export const syncUnbounceLeads = async () => {
  try {
    const response = await unbounceClient.post('/admin/unbounce/sync');
    return response.data;
  } catch (error) {
    if (error.needsReconnect) {
      // Try to reconnect
      await reconnectUnbounce();
      // Retry the original request
      return unbounceClient.post('/admin/unbounce/sync');
    }
    throw error;
  }
};

/**
 * Get Unbounce analytics data
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} The analytics data
 */
export const getUnbounceAnalytics = async (params = {}) => {
  const cacheKey = `analytics-${JSON.stringify(params)}`;
  const cachedData = cache.get(cacheKey);
  
  if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
    return cachedData.data;
  }
  
  try {
    const response = await unbounceClient.get('/admin/unbounce/analytics', { params });
    const data = response.data;
    
    cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    return data;
  } catch (error) {
    if (error.needsReconnect) {
      // Try to reconnect
      await reconnectUnbounce();
      // Retry the original request
      const response = await unbounceClient.get('/admin/unbounce/analytics', { params });
      const data = response.data;
      
      cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });
      
      return data;
    }
    throw error;
  }
}; 